/*
 * Copyright 2022-present terrestris GmbH & Co. KG
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 * list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 * this list of conditions and the following disclaimer in the documentation
 * and/or other materials provided with the distribution.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 *
 */
import React, {
  useEffect
} from 'react';

import OlLayerImage from 'ol/layer/Image';
import OlLayerTile from 'ol/layer/Tile';
import OlSourceImageWMS from 'ol/source/ImageWMS';
import OlSourceTileWMS from 'ol/source/TileWMS';

import {
  useTranslation
} from 'react-i18next';

import MapUtil from '@terrestris/ol-util/dist/MapUtil/MapUtil';

import CoordinateInfo, {
  CoordinateInfoState,
  CoordinateInfoProps
} from '@terrestris/react-geo/dist/CoordinateInfo/CoordinateInfo';
import {
  useMap
} from '@terrestris/react-geo/dist/Hook/useMap';
import {
  WmsLayer
} from '@terrestris/react-geo/dist/Util/typeUtils';

import {
  useAppDispatch
} from '../../../hooks/useAppDispatch';
import {
  useAppSelector
} from '../../../hooks/useAppSelector';
import {
  toggleVisibility
} from '../../../store/drawer';

import FeaturePropertyGrid from '../FeaturePropertyGrid';

import './index.less';

export type FeatureInfoProps = {
  enabled?: boolean;
} & Partial<CoordinateInfoProps>;

export const FeatureInfo: React.FC<FeatureInfoProps> = ({
  enabled,
  ...restProps
}): JSX.Element => {
  const {
    t
  } = useTranslation();

  const map = useMap();
  const dispatch = useAppDispatch();
  const featureInfoActive = useAppSelector((state) => state.featureInfo.active);

  if (!map) {
    return <></>;
  }

  useEffect(() => {
    map.on('click', () => {
      // determination drawer state via `state.drawer.visible` is not reliable
      const drawerOpen = document.querySelector('.ant-drawer-open.side-drawer');
      if (featureInfoActive && !drawerOpen) {
        dispatch(toggleVisibility());
      }
    });
  }, []);

  const getQueryLayers = MapUtil.getAllLayers(map)
    .filter((layer) => {
      if (!layer.get('hoverable')) {
        return false;
      }

      if (layer instanceof OlLayerImage && layer.getSource() instanceof OlSourceImageWMS) {
        return true;
      }

      if (layer instanceof OlLayerTile && layer.getSource() instanceof OlSourceTileWMS) {
        return true;
      }

      return false;
    }) as WmsLayer[];

  const resultRenderer = (opts: CoordinateInfoState) => {
    const features = opts.features;
    const loading = opts.loading;

    if (Object.keys(features).length === 0) {
      return (
        <span className='usage-hint'>
          {t('FeatureInfo.usageHint')}
        </span>
      );
    }

    return Object.entries(features)
      .map(([layerName, feats]) => {
        return (
          <div
            key={layerName}
          >
            <FeaturePropertyGrid
              features={feats}
              layerName={layerName}
              loading={loading}
            />
          </div>
        );
      });
  };

  if (!enabled) {
    return <></>;
  }

  return (
    <div className='feature-info-panel'>
      <CoordinateInfo
        featureCount={10}
        map={map}
        queryLayers={getQueryLayers}
        resultRenderer={resultRenderer}
        {...restProps}
      />
    </div>
  );
};

export default FeatureInfo;
